/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component
} from '@angular/core';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	StatusReasonsDirective
} from '@insurance/directives/status-reasons.directive';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	EntityType
} from '@shared/implementations/entities/entity-type';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';

/* eslint-enable max-len */

@Component({
	selector: 'transaction-rescind-cancel',
	templateUrl: './transaction-rescind-cancel.component.html'
})

/**
 * A component representing a wizard step to rescind a cancelled transaction.
 *
 * @export
 * @class TransactionRescindCancelComponent
 * @extends {StatusReasonsDirective}
 */
export class TransactionRescindCancelComponent
	extends StatusReasonsDirective
{
	/**
	 * Gets or sets a client message to display if this wizard is not valid
	 * to be completed.
	 *
	 * @type {string}
	 * @memberof TransactionRescindCancelComponent
	 */
	public clientMessage: string =
		'Rescind cancellation is not allowed on a non-active policy term, '
			+ 'a policy term that is not the most recent, or when '
			+ 'the latest is not a cancellation transaction.';

	/**
	 * Gets or sets true/false if the current policy term is allowing the
	 * rescind cancellation process.
	 *
	 * @type {boolean}
	 * @memberof TransactionCancelReasonsComponent
	 */
	public allowedRescindCancellation: boolean;

	/**
	 * Gets or sets the policy term transaction entity instance associated to
	 * the current policy term data.
	 *
	 * @type {IEntityInstance}
	 * @memberof TransactionEndorseReasonsComponent
	 */
	public policyTermTransactionInstance: IEntityInstance;

	/**
	 * Allows additional actions following the shared status reasons directive
	 * on init.
	 *
	 * @memberof TransactionRescindCancelComponent
	 */
	public async performPostInitActions(): Promise<void>
	{
		this.allowedRescindCancellation =
			await this.isWizardStepValidForDisplay();

		this.workflowActionName =
			InsuranceConstants.workflowActions.termRescindCancel;

		this.context.source.addOrUpdateStepData(
			{
				comments: AppConstants.empty
			});

		this.context.source.addToNext(this.rescindCancel.bind(this));
	}

	/**
	 * This will send the transaction rescind cancel event and navigate to the
	 * policy term dashboard.
	 *
	 * @async
	 * @memberof TransactionRescindCancelComponent
	 */
	public async rescindCancel(): Promise<void>
	{
		await this.transactionRescindCancel();
		this.navigateToPolicyTermDashboard();
	}

	/**
	 * Executes the transaction rescind cancellation process by executing the
	 * transactionRescindCancel workflow action to update the associated Policy
	 * entity data with the collected data from the Transaction Rescind Cancel
	 * Wizard.
	 *
	 * @async
	 * @memberof TransactionRescindCancelComponent
	 */
	public async transactionRescindCancel(): Promise<void>
	{
		const displayName: string =
			new EntityType(this.insuranceEntityTypes.policyTermEntityType)
				.displayName;
		const currentData: any =
			this.context.source.activeMenuItem.currentData.data;

		setTimeout(
			() =>
			{
				this.context.source.wizardStepLoading = true;
			});

		return this.activityService.handleActivity(
			new Activity(
				new Promise(async(resolve: any, _reject: any) =>
				{
					const queryString: string =
						`?comments=${currentData.comments}`;

					await this.executeWorkflowAction(
						this.insuranceEntityTypes.policyTermEntityType,
						this.policyTermEntityInstance,
						queryString);

					resolve();
				}),
				'<strong>Rescinding cancellation</strong>',
				'<strong>Cancellation rescinded</strong>',
				`Cancellation has been rescinded for ${displayName}.`,
				`Cancellation has not been rescinded for ${displayName}.`));
	}

	/**
	 * Validates the wizard step based on the component logic to
	 * confirm if this should be displayed or not.
	 *
	 * @async
	 * @returns {Promise<boolean>}
	 * An awaitable promise that returns a value signifying whether or not
	 * the wizard step is valid for display.
	 * @memberof TransactionCancelReasonsComponent
	 */
	public async isWizardStepValidForDisplay(): Promise<boolean>
	{
		const policy: IEntityInstance =
			await this.insuranceService.getPolicyByPolicyTermId(
				this.policyTermEntityInstance.id);

		if (AnyHelper.isNull(policy))
		{
			return false;
		}

		const activePolicyTerm: IEntityInstance =
			await this.insuranceService.getActivePolicyTerm(
				policy.id);

		const policyTermTransactionTypeGroup: string =
			'PolicyTermTransaction.'
				+ `${this.policyTermEntityInstance.data.productName}`;

		this.entityInstanceApiService.entityInstanceTypeGroup =
			this.insuranceEntityTypes.policyTermEntityType.group;

		const latestTransactions: IEntityInstance[] =
			await this.entityInstanceApiService.getChildren(
				this.policyTermEntityInstance.id,
				`${AppConstants.commonProperties.status} eq `
					+ `'${InsuranceConstants.transactionStatusTypes.issued}' ` +
				`and ${AppConstants.commonProperties.type} eq `
					+ `'${InsuranceConstants.transactionTypes.cancellation}'`,
				`${InsuranceConstants.commonProperties.effectiveDate} `
					+ `${AppConstants.sortDirections.descending}, `
					+ `${AppConstants.commonProperties.id} `
					+ `${AppConstants.sortDirections.descending}`,
				0,
				AppConstants.dataLimits.large,
				policyTermTransactionTypeGroup);

		if (latestTransactions.length > 0)
		{
			this.policyTermTransactionInstance = latestTransactions[0];
		}

		const result: boolean =
			!(this.policyTermEntityInstance.data.status !==
				InsuranceConstants.policyTermStatusTypes.active
					|| activePolicyTerm.data.renewalTerm >
						this.policyTermEntityInstance.data.renewalTerm
					|| latestTransactions.length === 0);

		return result;
	}

	/**
	 * This will navigate to the parent policy term dashboard.
	 *
	 * @memberof TransactionRescindCancelComponent
	 */
	private navigateToPolicyTermDashboard(): void
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		const currentData: any =
			this.context.source.activeMenuItem.currentData.data;

		this.router.navigate(
			[
				`${AppConstants.moduleNames.policy}/entities`,
				this.insuranceEntityTypes.policyTermEntityType.group,
				AppConstants.viewTypes.edit,
				currentData.id
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}
}